import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CheckCircle2, Rocket, Loader2 } from 'lucide-react'
import type { OnboardingProgress } from '@/lib/api/services/onboarding'

interface CompletionStepProps {
  progress: OnboardingProgress
  onComplete: () => void
  disabled?: boolean
}

export function CompletionStep({ progress, onComplete, disabled }: CompletionStepProps) {
  // Summarize choices
  const choices = [
    { label: 'Account Type', value: progress.accountType },
    progress.selectedPresetId && { label: 'Industry Preset', value: 'Selected' },
    progress.selectedTier && { label: 'Plan', value: progress.selectedTier.charAt(0).toUpperCase() + progress.selectedTier.slice(1) },
    progress.trialEnabled && progress.selectedTier !== 'free' && { label: 'Trial', value: '14 days free' }
  ].filter(Boolean) as { label: string; value: string }[]

  return (
    <div className="space-y-8 text-center">
      {/* Success icon */}
      <div className="flex justify-center">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
          <CheckCircle2 className="h-10 w-10 text-green-600" />
        </div>
      </div>

      {/* Congratulations message */}
      <div>
        <h2 className="text-2xl font-semibold text-slate-900 mb-2">
          You're all set!
        </h2>
        <p className="text-slate-600">
          Your workspace is ready. Here's a summary of your choices:
        </p>
      </div>

      {/* Summary */}
      <div className="flex flex-wrap justify-center gap-2">
        {choices.map((choice) => (
          <Badge key={choice.label} variant="secondary" className="text-sm py-1 px-3">
            {choice.label}: {choice.value}
          </Badge>
        ))}
        {progress.includeDefaultAgents && (
          <Badge variant="secondary" className="text-sm py-1 px-3">
            6 AI Agents included
          </Badge>
        )}
      </div>

      {/* What's next */}
      <div className="max-w-md mx-auto text-left bg-slate-50 rounded-lg p-6">
        <h3 className="font-medium text-slate-900 mb-4">What's next?</h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm flex-shrink-0">
              1
            </span>
            <span className="text-slate-600">
              Explore your dashboard and get familiar with the interface
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm flex-shrink-0">
              2
            </span>
            <span className="text-slate-600">
              Create your first template or import existing documents
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm flex-shrink-0">
              3
            </span>
            <span className="text-slate-600">
              Try out the AI agents to supercharge your productivity
            </span>
          </li>
        </ul>
      </div>

      {/* CTA */}
      <div className="pt-4">
        <Button onClick={onComplete} disabled={disabled} size="lg" className="gap-2">
          {disabled ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Finalizing...
            </>
          ) : (
            <>
              <Rocket className="h-5 w-5" />
              Go to Dashboard
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
